// src/pages/AdminPage/modals/EditTopicModal.tsx
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import type { TopicItem } from "../utils/adminTypes";

type TopicForm = Omit<TopicItem, "id" | "created_at" | "question_count">;

interface EditTopicModalProps {
  topic: TopicItem | null; // null = yangi yaratish, object = tahrirlash
  open: boolean;
  onClose: () => void;
  onSave: (id: number | null, data: TopicForm) => Promise<void>;
}

const emptyForm: TopicForm = {
  name_oz: "",
  name_uz: "",
  name_ru: "",
  description_oz: "",
  description_uz: "",
  description_ru: "",
};

export default function EditTopicModal({
  topic,
  open,
  onClose,
  onSave,
}: EditTopicModalProps) {
  const [form, setForm] = useState<TopicForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (topic) {
      setForm({
        name_oz: topic.name_oz || "",
        name_uz: topic.name_uz || "",
        name_ru: topic.name_ru || "",
        description_oz: topic.description_oz || "",
        description_uz: topic.description_uz || "",
        description_ru: topic.description_ru || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [topic, open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.name_oz.trim()) {
      return;
    }
    setSaving(true);
    try {
      await onSave(topic?.id || null, form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "#0D1526",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 13,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          padding: 24,
          width: "100%",
          maxWidth: 500,
          maxHeight: "90vh",
          overflowY: "auto",
        }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}>
          <h3
            style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: 0 }}>
            {topic ? "Mavzuni tahrirlash" : "Yangi mavzu"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: "pointer",
            }}>
            <X size={18} />
          </button>
        </div>

        {/* Names */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Nomi (O'zbek lotin) *</label>
          <input
            value={form.name_oz}
            onChange={(e) => setForm({ ...form, name_oz: e.target.value })}
            style={inputStyle}
            placeholder="Asosiy qoidalar"
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Nomi (O'zbek kirill)</label>
          <input
            value={form.name_uz}
            onChange={(e) => setForm({ ...form, name_uz: e.target.value })}
            style={inputStyle}
            placeholder="Асосий қоидалар"
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Nomi (Rus)</label>
          <input
            value={form.name_ru}
            onChange={(e) => setForm({ ...form, name_ru: e.target.value })}
            style={inputStyle}
            placeholder="Основные правила"
          />
        </div>

        {/* Descriptions */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Tavsif (O'zbek lotin)</label>
          <textarea
            value={form.description_oz}
            onChange={(e) =>
              setForm({ ...form, description_oz: e.target.value })
            }
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Tavsif (O'zbek kirill)</label>
          <textarea
            value={form.description_uz}
            onChange={(e) =>
              setForm({ ...form, description_uz: e.target.value })
            }
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Tavsif (Rus)</label>
          <textarea
            value={form.description_ru}
            onChange={(e) =>
              setForm({ ...form, description_ru: e.target.value })
            }
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name_oz.trim()}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              cursor:
                saving || !form.name_oz.trim() ? "not-allowed" : "pointer",
              opacity: saving || !form.name_oz.trim() ? 0.7 : 1,
            }}>
            {saving ?
              "Saqlanmoqda..."
            : topic ?
              "Saqlash"
            : "Yaratish"}
          </button>
        </div>
      </div>
    </div>
  );
}
